import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import SearchResults from './components/SearchResults';
import ConnectionRequestModal from './components/ConnectionRequestModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const AdvancedSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    partner_type: [],
    musical_specialties: [],
    artist_profession: [],
    location: '',
    experience_level: [],
    availability_status: [],
    verified_only: false
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 12;

  // Get current user on component mount
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const { data: userProfile } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();
          setCurrentUser(userProfile);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    getCurrentUser();
    loadSearchHistory();
    loadSavedSearches();
  }, []);

  // Load search history from localStorage
  const loadSearchHistory = () => {
    try {
      const history = localStorage.getItem('search_history');
      if (history) {
        setSearchHistory(JSON.parse(history));
      }
    } catch (error) {
      console.error('Error loading search history:', error);
    }
  };

  // Load saved searches from localStorage
  const loadSavedSearches = () => {
    try {
      const saved = localStorage.getItem('saved_searches');
      if (saved) {
        setSavedSearches(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved searches:', error);
    }
  };

  // Save search to history
  const saveToHistory = (query, resultsCount) => {
    if (!query?.trim()) return;
    
    const newSearch = {
      id: Date.now(),
      query: query?.trim(),
      resultsCount,
      timestamp: new Date()?.toISOString(),
      filters: { ...filters }
    };

    const updatedHistory = [newSearch, ...searchHistory?.slice(0, 9)];
    setSearchHistory(updatedHistory);
    localStorage.setItem('search_history', JSON.stringify(updatedHistory));
  };

  // Perform search
  const performSearch = async (page = 1) => {
    setIsLoading(true);
    setCurrentPage(page);

    try {
      // Build the search query
      let query = supabase?.from('partners')?.select(`
          *,
          user_profiles!inner(
            id,
            full_name,
            email,
            avatar_url,
            role
          ),
          partner_musical_specialties(
            specialty
          ),
          partner_profession_data(
            profession,
            skill_tags,
            performance_styles,
            equipment_list,
            specialization_data
          )
        `)?.eq('status', 'active');

      // Apply filters
      if (filters?.partner_type?.length > 0) {
        query = query?.in('partner_type', filters?.partner_type);
      }

      if (filters?.artist_profession?.length > 0) {
        query = query?.in('artist_profession', filters?.artist_profession);
      }

      if (filters?.availability_status?.length > 0) {
        query = query?.in('availability_status', filters?.availability_status);
      }

      if (filters?.location?.trim()) {
        query = query?.or(`city.ilike.%${filters?.location}%,country.ilike.%${filters?.location}%`);
      }

      if (filters?.verified_only) {
        query = query?.eq('is_verified', true);
      }

      // Apply text search if query exists
      if (searchQuery?.trim()) {
        query = query?.or(`
          business_name.ilike.%${searchQuery}%,
          description.ilike.%${searchQuery}%,
          professional_bio.ilike.%${searchQuery}%,
          user_profiles.full_name.ilike.%${searchQuery}%
        `);
      }

      // Get total count first
      const { count } = await query?.select('*', { count: 'exact', head: true });
      setTotalResults(count || 0);

      // Apply pagination
      const startIndex = (page - 1) * resultsPerPage;
      query = query?.range(startIndex, startIndex + resultsPerPage - 1);

      // Order by relevance and rating
      query = query?.order('average_rating', { ascending: false })?.order('total_bookings', { ascending: false });

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Filter by musical specialties if specified
      let filteredResults = data || [];
      if (filters?.musical_specialties?.length > 0) {
        filteredResults = filteredResults?.filter(partner => 
          partner?.partner_musical_specialties?.some(spec => 
            filters?.musical_specialties?.includes(spec?.specialty)
          )
        );
      }

      // Filter by experience level if specified
      if (filters?.experience_level?.length > 0) {
        filteredResults = filteredResults?.filter(partner => {
          const years = partner?.years_experience || 0;
          return filters?.experience_level?.some(level => {
            switch (level) {
              case 'beginner': return years < 2;
              case 'intermediate': return years >= 2 && years < 5;
              case 'experienced': return years >= 5 && years < 10;
              case 'expert': return years >= 10;
              default: return true;
            }
          });
        });
      }

      setSearchResults(filteredResults);
      
      // Save to search history
      if (searchQuery?.trim()) {
        saveToHistory(searchQuery, filteredResults?.length);
      }

    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search submission
  const handleSearch = (query) => {
    setSearchQuery(query);
    performSearch(1);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (searchQuery || Object.values(newFilters)?.some(val => 
      Array.isArray(val) ? val?.length > 0 : val
    )) {
      performSearch(1);
    }
  };

  // Handle connection request
  const handleConnectionRequest = (partner) => {
    setSelectedPartner(partner);
    setShowConnectionModal(true);
  };

  // Handle save search
  const handleSaveSearch = () => {
    if (!searchQuery?.trim() && !Object.values(filters)?.some(val => 
      Array.isArray(val) ? val?.length > 0 : val
    )) {
      return;
    }

    const newSavedSearch = {
      id: Date.now(),
      name: searchQuery || 'Recherche personnalisée',
      query: searchQuery,
      filters: { ...filters },
      created_at: new Date()?.toISOString(),
      results_count: totalResults
    };

    const updatedSaved = [newSavedSearch, ...savedSearches?.slice(0, 9)];
    setSavedSearches(updatedSaved);
    localStorage.setItem('saved_searches', JSON.stringify(updatedSaved));
  };

  // Handle load saved search
  const handleLoadSavedSearch = (savedSearch) => {
    setSearchQuery(savedSearch?.query || '');
    setFilters(savedSearch?.filters || {});
    performSearch(1);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    performSearch(page);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalResults / resultsPerPage);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Recherche avancée 🔍
                </h1>
                <p className="text-muted-foreground">
                  Trouvez des artistes, agents, et professionnels de la musique
                </p>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Save"
                  iconPosition="left"
                  iconSize={16}
                  onClick={handleSaveSearch}
                  disabled={!searchQuery?.trim() && !Object.values(filters)?.some(val => 
                    Array.isArray(val) ? val?.length > 0 : val
                  )}
                >
                  Sauvegarder
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="History"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => document.getElementById('search-history')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Historique
                </Button>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <SearchBar
              onSearch={handleSearch}
              searchQuery={searchQuery}
              isLoading={isLoading}
            />
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filter Panel */}
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                searchHistory={searchHistory}
                savedSearches={savedSearches}
                onLoadSavedSearch={handleLoadSavedSearch}
              />
            </div>

            {/* Search Results */}
            <div className="lg:col-span-3">
              {/* Results Header */}
              {(searchQuery || Object.values(filters)?.some(val => 
                Array.isArray(val) ? val?.length > 0 : val
              )) && (
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground">
                      Résultats de recherche
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {totalResults} résultat{totalResults !== 1 ? 's' : ''} trouvé{totalResults !== 1 ? 's' : ''}
                      {searchQuery && ` pour "${searchQuery}"`}
                    </p>
                  </div>
                  
                  {/* Pagination Info */}
                  {totalPages > 1 && (
                    <div className="mt-2 sm:mt-0 text-sm text-muted-foreground">
                      Page {currentPage} sur {totalPages}
                    </div>
                  )}
                </div>
              )}

              <SearchResults
                results={searchResults}
                isLoading={isLoading}
                onConnectionRequest={handleConnectionRequest}
                currentUser={currentUser}
              />

              {/* Pagination */}
              {totalPages > 1 && !isLoading && (
                <div className="mt-8 flex justify-center">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ChevronLeft"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    
                    {[...Array(Math.min(5, totalPages))]?.map((_, i) => {
                      const pageNumber = Math.max(1, currentPage - 2) + i;
                      if (pageNumber > totalPages) return null;
                      
                      return (
                        <Button
                          key={pageNumber}
                          variant={currentPage === pageNumber ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Button>
                      );
                    })}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ChevronRight"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Empty State */}
          {!searchQuery && !Object.values(filters)?.some(val => 
            Array.isArray(val) ? val?.length > 0 : val
          ) && !isLoading && (
            <div className="text-center py-12">
              <Icon name="Search" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-card-foreground mb-2">
                Commencez votre recherche
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Utilisez la barre de recherche ci-dessus ou les filtres pour trouver des artistes, agents et professionnels de la musique.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {['Artistes', 'Agents', 'DJ', 'Musiciens', 'Chanteurs']?.map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearch(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      {/* Connection Request Modal */}
      {showConnectionModal && selectedPartner && (
        <ConnectionRequestModal
          partner={selectedPartner}
          currentUser={currentUser}
          onClose={() => {
            setShowConnectionModal(false);
            setSelectedPartner(null);
          }}
        />
      )}
    </div>
  );
};

export default AdvancedSearch;